import React, { useEffect, useState } from 'react';
import { Button, Table } from 'antd';
import { PlusOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import AppShell from './components/AppShell';
import Panel from './components/Panel';
import TableSkeleton from './components/TableSkeleton';
import { Badge } from './components/StatusPill';
import axiosInstance from './utils/axiosInstance';
import { money } from './utils/format';
import Swal from 'sweetalert2';
import { confirmDialog, errorDialog, successDialog } from './utils/dialog';
import { useNavigate, useParams } from 'react-router-dom';

const { Column } = Table;

interface UserProductType {
    key: React.Key;
    id: number;
    amount: number;
    unit: number;
    productType: string;
    product: {
        img: string;
        name: string;
        seller: string;
    };
}


const UserProductPage: React.FC = () => {
    const { id } = useParams();

    const [userProducts, setUserProducts] = useState<UserProductType[]>([]);
    const [loading, setLoading] = useState(true);
    const [productSets, setProductSets] = useState<any[]>([]);

    const navigate = useNavigate();

    const getUserProducts = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.post("admin/protected/getUserProduct", {
                userId: id,
            })
            const data = response?.data?.data;
            if (data) {
                console.log(data)
                setUserProducts(data.map((e: any) => {
                    return {
                        id: e.id,
                        key: e.id,
                        amount: e.amount,
                        unit: e.unit,
                        productType: e?.productType,
                        product: {
                            img: e.Product?.img,
                            name: e.Product?.name,
                            seller: e.Product?.seller,
                        }
                    } as UserProductType;
                }));
            }
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        getUserProducts();
        axiosInstance.post('admin/protected/getProductSets').then(res => {
            if (res.data?.data) {
                setProductSets(res.data.data);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const deleteUserProduct = async (data: UserProductType) => {
        if (!await confirmDialog({ title: '确定删除？', text: `删除 ${data.product.name}` })) return;
        try {
            await axiosInstance.post("admin/protected/deleteUserProduct", {
                id: data.id,
            })
            getUserProducts();
        } catch (error: any) {
            errorDialog();
        }
    }

    const assignSet = async () => {
        const options: Record<string, string> = {};
        productSets.forEach(s => {
            options[s.id] = `${s.name} (${s.commission}%)`;
        });

        if (Object.keys(options).length === 0) {
            errorDialog('暂无配套，请先创建');
            return;
        }

        const { value: setId } = await Swal.fire({
            title: '选择配套',
            input: 'select',
            inputOptions: options,
            inputPlaceholder: '选择配套',
            showCancelButton: true,
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            inputValidator: (value) => {
                return new Promise((resolve) => {
                    if (value) {
                        resolve(null);
                    } else {
                        resolve('请选择配套');
                    }
                });
            }
        });

        if (setId) {
            const selectedSet = productSets.find(s => s.id === Number(setId));
            if (!selectedSet) return;
            try {
                await axiosInstance.post('admin/protected/addUserSet', {
                    userId: id,
                    commission: selectedSet.commission,
                    set: selectedSet.id,
                });
                getUserProducts();
                successDialog('配套已分配');
            } catch (error) {
                errorDialog('操作失败');
            }
        }
    }

    return (
        <AppShell
            title="设置连单"
            subtitle={`用户 #${id} · ${userProducts.length} 连单`}
            actions={
                <>
                    <Button icon={<PlusOutlined />} type="primary" onClick={() => navigate(`/newuserproduct/${id}`)}>新建</Button>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/user`)}>返回</Button>
                </>
            }
        >
            <Panel className="mb-4">
                <div className='flex flex-wrap items-center gap-2'>
                    <span className="mr-1 text-[13px] text-muted">快速设置:</span>
                    <Button onClick={() => assignSet()}>分配配套</Button>
                </div>
            </Panel>

            <Panel flush>
            {loading ? <TableSkeleton rows={4} columns={6} /> : (
            <Table<UserProductType> dataSource={userProducts} scroll={{ x: 'max-content' }} size="middle">
                <Column title="ID" dataIndex="id" key="id" width={70} />
                <Column
                    title="照片"
                    key="image"
                    width={110}
                    render={(_: any, record: UserProductType) => (
                        <div className="group h-[72px] w-[72px] overflow-hidden rounded-lg border border-line
                                        shadow-soft transition-shadow duration-250 hover:shadow-lifted">
                            <img
                                alt={record.product.name}
                                src={`${process.env.REACT_APP_FOLDER_URL}/${record.product.img}`}
                                className="h-full w-full object-cover transition-transform duration-300
                                           ease-smooth group-hover:scale-110"
                            />
                        </div>
                    )}
                />
                <Column
                    title="名称"
                    key="name"
                    render={(_: any, record: UserProductType) => (
                        <span className="font-semibold text-ink">{record.product.name}</span>
                    )}
                />
                <Column
                    title="卖家"
                    key="seller"
                    render={(_: any, record: UserProductType) => (
                        <span className="text-muted">{record.product.seller}</span>
                    )}
                />
                <Column
                    title="类型"
                    key="productType"
                    render={(_: any, record: UserProductType) => (
                        record.productType === 'premium'
                            ? <Badge tone="caution">高级</Badge>
                            : <Badge tone="info">普通</Badge>
                    )}
                />
                <Column title="金额" key="amount" className='tabular'
                    render={(_: any, record: UserProductType) => (
                        <span className="font-semibold text-ink">
                            <span className="mr-1 text-xs font-normal text-faint">RM</span>
                            {money(record.amount)}
                        </span>
                    )}
                />
                <Column title="数量" dataIndex="unit" key="unit" className='tabular' />
                <Column
                    title="操作"
                    key="action"
                    fixed="right"
                    width={110}
                    render={(_: any, record: UserProductType) => (
                        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteUserProduct(record)}>删除</Button>
                    )}
                />
            </Table>
            )}
            </Panel>
        </AppShell>
    );
}

export default UserProductPage;
